import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, Plus, Edit, Trash2, Eye, Key, CheckCircle, XCircle, Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    role: "user",
    is_active: true,
    active_until: ""
  });

  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirm_password: ""
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      if (response.data.status === "success") {
        setUsers(response.data.users);
      }
    } catch (error) {
      toast.error("Fout bij laden gebruikers");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!userForm.email || !userForm.password) {
      toast.error("Email en wachtwoord zijn verplicht");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, userForm, {
        withCredentials: true
      });
      toast.success("Gebruiker aangemaakt!");
      setIsDialogOpen(false);
      resetUserForm();
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fout bij aanmaken gebruiker";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates, {
        withCredentials: true
      });
      toast.success("Gebruiker bijgewerkt!");
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fout bij bijwerken gebruiker";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (userId) => {
    if (!passwordForm.password) {
      toast.error("Wachtwoord is verplicht");
      return;
    }

    if (passwordForm.password !== passwordForm.confirm_password) {
      toast.error("Wachtwoorden komen niet overeen");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/${userId}/set-password`, {
        password: passwordForm.password
      }, {
        withCredentials: true
      });
      toast.success("Wachtwoord ingesteld!");
      setIsPasswordDialogOpen(false);
      setSelectedUser(null);
      setPasswordForm({ password: "", confirm_password: "" });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fout bij instellen wachtwoord";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Weet je zeker dat je ${userEmail} wilt verwijderen?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        withCredentials: true
      });
      toast.success("Gebruiker verwijderd!");
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fout bij verwijderen gebruiker";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (userId, userEmail) => {
    if (!window.confirm(`Wil je het account van ${userEmail} bekijken?`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/impersonate/${userId}`, {}, {
        withCredentials: true
      });
      toast.success(`Je bekijkt nu ${userEmail}`);
      // Reload page to refresh data
      setTimeout(() => window.location.href = "/", 1000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Fout bij impersonate";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      email: "",
      password: "",
      role: "user",
      is_active: true,
      active_until: ""
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const isExpired = (activeUntil) => {
    if (!activeUntil) return false;
    return new Date(activeUntil) < new Date();
  };

  return (
    <div className="container mx-auto py-4 sm:py-8 px-3 sm:px-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-xl sm:text-2xl flex items-center gap-2">
                <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                Gebruikersbeheer
              </CardTitle>
              <CardDescription className="text-sm">
                Beheer gebruikers, rollen en toegang
              </CardDescription>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetUserForm} className="text-sm w-full sm:w-auto">
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  Nieuwe Gebruiker
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nieuwe Gebruiker Aanmaken</DialogTitle>
                  <DialogDescription>
                    Maak een nieuwe gebruiker aan met email en wachtwoord
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      placeholder="gebruiker@email.nl"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Wachtwoord</Label>
                    <Input
                      id="password"
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Rol</Label>
                    <Select
                      value={userForm.role}
                      onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="active_until">Actief tot (optioneel)</Label>
                    <Input
                      id="active_until"
                      type="date"
                      value={userForm.active_until}
                      onChange={(e) => setUserForm({ ...userForm, active_until: e.target.value })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={loading}>
                      Aanmaken
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Annuleren
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          <div className="overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actief tot</TableHead>
                <TableHead>Laatst ingelogd</TableHead>
                <TableHead>Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <Badge variant="success" className="flex items-center gap-1 w-fit">
                        <CheckCircle className="h-3 w-3" />
                        Actief
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                        <XCircle className="h-3 w-3" />
                        Inactief
                      </Badge>
                    )}
                    {isExpired(user.active_until) && (
                      <Badge variant="destructive" className="ml-2">Verlopen</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.active_until ? (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(user.active_until).toLocaleDateString('nl-NL')}
                      </span>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatDate(user.last_login_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImpersonate(user.id, user.email)}
                        title="Bekijk als deze gebruiker"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user);
                          setIsPasswordDialogOpen(true);
                        }}
                        title="Wachtwoord instellen"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateUser(user.id, { is_active: !user.is_active })}
                        title={user.is_active ? "Deactiveren" : "Activeren"}
                      >
                        {user.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        title="Verwijderen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wachtwoord Instellen</DialogTitle>
            <DialogDescription>
              Stel een nieuw wachtwoord in voor {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">Nieuw Wachtwoord</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.password}
                onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Bevestig Wachtwoord</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleSetPassword(selectedUser?.id)} disabled={loading}>
                Instellen
              </Button>
              <Button variant="outline" onClick={() => {
                setIsPasswordDialogOpen(false);
                setSelectedUser(null);
                setPasswordForm({ password: "", confirm_password: "" });
              }}>
                Annuleren
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
